import { db } from './db.js';
import { catalogStore } from './catalog-store.js';
import { opaqueId, extOf, normalizeText } from './utils.js';

/* Archivos propios: una carpeta de vídeos publicada por web (índice de directorio de un NAS o de
   un servidor) o por WebDAV (Nextcloud, Synology…). El catálogo sale de los nombres de los
   ficheros: «Título (Año).mkv» es una película y «Serie S01E02.mkv» un episodio. La carátula, la
   sinopsis y el reparto los pone TMDB, igual que en el resto de proveedores.

   Desde la web (https) solo se llega a carpetas publicadas en internet, y las peticiones pasan
   por el servidor de Filoteca. A una de la red local se llega desde la app de Android. */

export const VIDEO_EXT = ['mp4', 'mkv', 'avi', 'mov', 'webm', 'm4v', 'mpg', 'mpeg', 'ts', 'wmv', 'flv'];
const NOISE = /\b(2160p|1080p|720p|480p|4k|uhd|fhd|hdr10?|hdr|dv|x26[45]|h\.?26[45]|hevc|avc|web-?dl|web-?rip|bluray|blu-ray|bdrip|brrip|dvdrip|hdtv|remux|dual|multi|castellano|spanish|latino|vose|aac|ac3|eac3|dts|atmos|10bit|proper|repack|extended|unrated)\b.*$/i;
const LIMITS = { depth: 5, dirs: 300, files: 5000 };

/* TMDB siempre devuelve «lo más parecido», aunque no tenga nada que ver: un vídeo de la cámara web en una carpeta «personal» salía
   como la película «Personal Assistant». Solo se acepta lo que TMDB devuelve si el título se parece de verdad al del fichero. */
const STOP_WORDS = new Set(['the', 'a', 'an', 'el', 'la', 'los', 'las', 'un', 'una', 'de', 'del', 'y', 'and', 'of', 'le', 'les', 'et', 'der', 'die', 'das']);
const titleTokens = (s) => normalizeText(String(s || '')).replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter((w) => w && !STOP_WORDS.has(w));

/** ¿El resultado de TMDB (`x`) es de verdad el título de este fichero (`item`)? Compara con el título y con el original. */
export function confidentMatch(item, x) {
    if (!x || !x.tmdbId) return false;
    const a = titleTokens(item && item.name);
    if (!a.length) return false;
    // Si el nombre trae año y el de TMDB difiere de más, es otra cosa.
    if (item.year && x.year && Math.abs(Number(item.year) - Number(x.year)) > 1) return false;
    return [x.title, x.originalTitle].filter(Boolean).map(titleTokens).some((b) => {
        if (!b.length) return false;
        const inTitle = a.filter((w) => b.indexOf(w) !== -1).length / a.length;
        const inFile = b.filter((w) => a.indexOf(w) !== -1).length / b.length;
        return inTitle >= 0.6 && inFile >= 0.6;
    });
}

const tidy = (text) => String(text || '').replace(/[._]+/g, ' ').replace(/\s+/g, ' ').replace(/^[\s\-–([]+|[\s\-–(\[]+$/g, '').trim();

/**
 * Nombre de fichero → qué es. Devuelve { title, year, season, episode, ext } (season y episode
 * solo en los episodios) o null si no es un vídeo.
 */
export function parseMediaName(name) {
    const ext = extOf(name);
    if (VIDEO_EXT.indexOf(ext) === -1) return null;
    const base = String(name).slice(0, -(ext.length + 1));
    const ep = /^(.*?)[\s._\-([]*(?:s(\d{1,2})[\s._-]*e(\d{1,3})|(\d{1,2})x(\d{2,3}))(?!\d)(.*)$/i.exec(base);
    if (ep) {
        const year = /\b((?:19|20)\d{2})\b/.exec(ep[1]);
        return {
            title: tidy(ep[1].replace(/[\s._\-([]*\(?(?:19|20)\d{2}\)?\s*$/, '')),
            year: year ? year[1] : '',
            season: Number(ep[2] || ep[4]),
            episode: Number(ep[3] || ep[5]),
            episodeTitle: tidy(ep[6].replace(NOISE, '')),
            ext
        };
    }
    // El año es el último que aparece: «2001 Una odisea del espacio (1968)».
    let year = '';
    let cut = base.length;
    const re = /[\s._\-([]+((?:19|20)\d{2})(?=[\s._\-)\]]|$)/g;
    let m;
    while ((m = re.exec(base))) {
        year = m[1];
        cut = m.index;
    }
    const title = tidy((year ? base.slice(0, cut) : base).replace(NOISE, ''));
    return { title: title || tidy(base), year, ext };
}

/** Índice de directorio en HTML (Apache, nginx, NAS) → [{ name, href, dir }]. */
export function parseIndexHtml(html, baseUrl) {
    const out = [];
    const seen = new Set();
    const base = new URL(baseUrl.replace(/\/?$/, '/'));
    const re = /<a\s[^>]*href\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
    let m;
    while ((m = re.exec(String(html || '')))) {
        const raw = (m[1] != null ? m[1] : m[2]).replace(/&amp;/g, '&');
        if (!raw || raw[0] === '?' || raw[0] === '#' || /^(mailto|javascript):/i.test(raw)) continue;
        let u;
        try {
            u = new URL(raw, base);
        } catch (e) {
            continue;
        }
        // Solo lo que cuelga de esta carpeta: fuera quedan «..», la raíz y los enlaces externos.
        if (u.origin !== base.origin || u.pathname.indexOf(base.pathname) !== 0 || u.pathname === base.pathname) continue;
        const rest = u.pathname.slice(base.pathname.length);
        const dir = /\/$/.test(rest);
        if (rest.replace(/\/$/, '').indexOf('/') !== -1) continue;
        u.search = '';
        u.hash = '';
        if (seen.has(u.pathname)) continue;
        seen.add(u.pathname);
        let name = rest.replace(/\/$/, '');
        try { name = decodeURIComponent(name); } catch (e) { /* se queda como viene */ }
        out.push({ name, href: u.toString(), dir });
    }
    return out;
}

/** Respuesta de WebDAV (PROPFIND, profundidad 1) → [{ name, href, dir, size }]. */
export function parseWebdav(xml, baseUrl) {
    const out = [];
    const base = new URL(baseUrl.replace(/\/?$/, '/'));
    const blocks = String(xml || '').split(/<(?:[a-z0-9]+:)?response[\s>]/i).slice(1);
    blocks.forEach((block) => {
        const href = /<(?:[a-z0-9]+:)?href[^>]*>([^<]+)</i.exec(block);
        if (!href) return;
        let u;
        try {
            u = new URL(href[1].trim().replace(/&amp;/g, '&'), base);
        } catch (e) {
            return;
        }
        if (u.pathname.replace(/\/$/, '') === base.pathname.replace(/\/$/, '')) return; // la propia carpeta
        if (u.pathname.indexOf(base.pathname) !== 0) return;
        const dir = /<(?:[a-z0-9]+:)?collection\b/i.test(block) || /\/$/.test(u.pathname);
        const size = /<(?:[a-z0-9]+:)?getcontentlength[^>]*>(\d+)</i.exec(block);
        const date = /<(?:[a-z0-9]+:)?getlastmodified[^>]*>([^<]+)</i.exec(block);
        let name = u.pathname.replace(/\/$/, '').split('/').pop();
        try { name = decodeURIComponent(name); } catch (e) { /* se queda como viene */ }
        if (dir && !/\/$/.test(u.pathname)) u.pathname += '/';
        out.push({ name, href: u.toString(), dir, size: size ? Number(size[1]) : 0, modified: date ? Math.floor(Date.parse(date[1]) / 1000) || 0 : 0 });
    });
    return out;
}

const label = (text) => {
    const s = tidy(text);
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/**
 * files: [{ name, href, path }] con `path` = carpetas desde la raíz (p. ej. ['Series', 'Breaking
 * Bad', 'Season 01']). Devuelve { vod, series, categories } con la forma del resto de catálogos.
 * La categoría es la carpeta de primer nivel.
 */
export function buildCatalog(files, { withAuth = (url) => url } = {}) {
    const vod = [];
    const shows = new Map();
    const cats = { vod: new Map(), series: new Map() };
    const catId = (kind, name) => {
        const key = name || 'Sin carpeta';
        if (!cats[kind].has(key)) cats[kind].set(key, { id: 'd-' + opaqueId(kind + key).slice(0, 8), name: key });
        return cats[kind].get(key).id;
    };
    files.forEach((f) => {
        const info = parseMediaName(f.name);
        if (!info) return;
        const path = f.path || [];
        const id = 'f' + opaqueId(path.concat([f.name]).join('/')).slice(0, 16);
        const url = withAuth(f.href);
        if (info.season != null) {
            // Sin título en el fichero («S01E02.mkv»), la serie es la carpeta que no es «Temporada 1».
            const folder = path.filter((p) => !/^(season|temporada|temp|s)[\s._-]*\d+$/i.test(p)).pop() || '';
            const title = info.title || label(parseFolder(folder).title);
            if (!title) return;
            const key = title.toLowerCase();
            if (!shows.has(key)) {
                shows.set(key, {
                    id: 's' + opaqueId('serie:' + key).slice(0, 16), name: title, poster: '', backdrop: '',
                    categoryId: catId('series', label(path[0] && path.length > 1 ? path[0] : '')), plot: '', cast: '', director: '', genre: '',
                    releaseDate: '', year: info.year || parseFolder(folder).year, rating: 0, added: 0, youtube: '', seasons: []
                });
            }
            const s = shows.get(key);
            let season = s.seasons.find((x) => x.number === info.season);
            if (!season) {
                season = { number: info.season, name: 'Temporada ' + info.season, poster: '', overview: '', episodes: [] };
                s.seasons.push(season);
            }
            if (season.episodes.some((e) => e.num === info.episode)) return;
            season.episodes.push({ id, seriesId: s.id, season: info.season, num: info.episode, title: info.episodeTitle || ('Episodio ' + info.episode), ext: info.ext, plot: '', durationSecs: 0, duration: '', thumb: '', rating: 0, airDate: '', url });
            return;
        }
        vod.push({ id, name: info.title, poster: '', categoryId: catId('vod', label(path[0] || '')), ext: info.ext, added: f.modified || 0, rating: 0, year: info.year, plot: '', genre: '', duration: '', tmdb: '', url, size: f.size || 0 });
    });
    const series = Array.from(shows.values());
    series.forEach((s) => {
        s.seasons.sort((a, b) => a.number - b.number);
        s.seasons.forEach((se) => se.episodes.sort((a, b) => a.num - b.num));
    });
    vod.sort((a, b) => a.name.localeCompare(b.name));
    series.sort((a, b) => a.name.localeCompare(b.name));
    const toList = (map) => Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    return { vod, series, categories: { live: [], vod: toList(cats.vod), series: toList(cats.series) } };
}

/** «Breaking Bad (2008)» → { title, year } */
function parseFolder(name) {
    const m = /^(.*?)[\s._\-([]*\(?((?:19|20)\d{2})\)?\s*$/.exec(String(name || ''));
    return m && m[1] ? { title: m[1], year: m[2] } : { title: name, year: '' };
}

function fail(code, status) {
    return Object.assign(new Error(code), { code, status });
}

export class FilesClient {
    /**
     * request(url, { method, headers }) → { status, text }: lo inyecta clients.js (directo y, si
     * el navegador no deja, a través del servidor). lookup(kind, item) consulta TMDB.
     */
    constructor(profile, { request = null, lookup = null, bridge = null } = {}) {
        this.profile = profile;
        this.root = String(profile.host || '').replace(/\/?$/, '/');
        this.request = request;
        this.lookup = lookup;
        // SMB, carpetas del aparato y nubes: las sirve el propio aparato (puente nativo) en una dirección local con un código en la ruta,
        // sin usuario ni contraseña en la dirección. bridge.ensure(perfil) la prepara y devuelve la raíz.
        this.bridge = bridge;
    }

    async _ready() {
        if (!this.bridge || this._rooted) return;
        this.root = String(await this.bridge.ensure(this.profile)).replace(/\/?$/, '/');
        this._rooted = true;
    }

    /** La dirección con el usuario y la contraseña dentro, para que el reproductor pueda pedirla. */
    withAuth(url) {
        if (this.bridge || !this.profile.username) return url;
        try {
            const u = new URL(url);
            u.username = this.profile.username;
            u.password = this.profile.password || '';
            return u.toString();
        } catch (e) {
            return url;
        }
    }

    _headers(extra) {
        const h = Object.assign({}, extra);
        if (!this.bridge && this.profile.username) h.Authorization = 'Basic ' + btoa(unescape(encodeURIComponent(this.profile.username + ':' + (this.profile.password || ''))));
        return h;
    }

    /** Contenido de una carpeta: primero por WebDAV y, si no lo es, leyendo su índice. */
    async list(url) {
        if (this._mode !== 'index') {
            const r = await this.request(url, { method: 'PROPFIND', headers: this._headers({ Depth: '1', 'Content-Type': 'application/xml' }) }).catch((e) => ({ status: 0, error: e }));
            if (r.status === 401 || r.status === 403) throw fail('auth', r.status);
            if (r.status === 207) {
                this._mode = 'webdav';
                return parseWebdav(r.text, url);
            }
            if (this._mode === 'webdav') throw fail('http', r.status);
        }
        const r = await this.request(url, { method: 'GET', headers: this._headers({ Accept: 'text/html' }) });
        if (r.status === 401 || r.status === 403) throw fail('auth', r.status);
        if (r.status < 200 || r.status >= 300) throw fail('http', r.status);
        this._mode = 'index';
        return parseIndexHtml(r.text, url);
    }

    _account() {
        return {
            user_info: { username: this.profile.username || '', auth: 1, status: 'Active', exp_date: null, max_connections: 0, active_cons: 0 },
            server_info: { url: this.bridge ? (this.profile.type === 'dropbox' || this.profile.type === 'onedrive' ? String(this.profile.username || '') : String(this.profile.host || '').replace(/^[a-z]+:\/\//i, '')) : this.root.replace(/^https?:\/\//, '').replace(/\/$/, ''), product: this.bridge ? ({ smb: 'SMB', dropbox: 'Dropbox', onedrive: 'OneDrive' }[this.profile.type] || 'Local') : this._mode === 'webdav' ? 'WebDAV' : 'Carpeta web' }
        };
    }

    /** Lee la carpeta. Si no hay en ella ni carpetas ni vídeos, no es una carpeta de vídeos. */
    async _root() {
        await this._ready();
        const entries = await this.list(this.root);
        if (!entries.some((e) => e.dir || VIDEO_EXT.indexOf(extOf(e.name)) !== -1)) throw fail('empty');
        return entries;
    }

    async auth() {
        await this._root();
        return this._account();
    }

    /** Recorre las carpetas (con límites) y devuelve los vídeos con la ruta de cada uno. */
    async crawl(rootEntries) {
        const files = [];
        const queue = [{ url: this.root, path: [], entries: rootEntries }];
        let dirs = 0;
        while (queue.length && dirs < LIMITS.dirs && files.length < LIMITS.files) {
            const batch = queue.splice(0, 4);
            dirs += batch.length;
            const lists = await Promise.all(batch.map((d) => (d.entries ? Promise.resolve(d.entries) : this.list(d.url)).then((entries) => ({ d, entries }), (e) => {
                if (d.path.length === 0) throw e; // la raíz tiene que poder leerse
                return { d, entries: [] };
            })));
            lists.forEach(({ d, entries }) => entries.forEach((e) => {
                if (e.dir) {
                    if (d.path.length < LIMITS.depth && !/^[.@#]/.test(e.name)) queue.push({ url: e.href, path: d.path.concat([e.name]) });
                } else if (VIDEO_EXT.indexOf(extOf(e.name)) !== -1 && files.length < LIMITS.files) {
                    files.push({ name: e.name, href: e.href, path: d.path, size: e.size || 0, modified: e.modified || 0 });
                }
            }));
        }
        return files;
    }

    async fetchCatalog() {
        const root = await this._root();
        const account = this._account();
        const files = await this.crawl(root);
        const data = buildCatalog(files, { withAuth: (u) => this.withAuth(u) });
        await this.decorate(data);
        return Object.assign({ updatedAt: Date.now(), account: { user: account.user_info, server: account.server_info }, live: [] }, data);
    }

    /**
     * Carátula, año, nota y género de TMDB para lo que se acaba de encontrar. Lo ya consultado se
     * guarda en el dispositivo, así que cada vez solo se pregunta por los títulos nuevos.
     */
    async decorate(data, { max = 200, budgetMs = 20000 } = {}) {
        if (this.profile && this.profile.personal && this.profile.personal !== '0') return data; // vídeos personales: tal cual, sin TMDB
        const key = 'files-meta-v2:' + this.profile.id; // v2: con la comprobación de que el título de TMDB se parece al del fichero
        const known = (await db.get('meta', key).catch(() => null)) || {};
        const todo = [];
        ['vod', 'series'].forEach((kind) => data[kind].forEach((item) => {
            const hit = known[kind + ':' + item.id];
            if (hit) Object.assign(item, hit);
            else if (this.lookup && todo.length < max) todo.push({ kind, item });
        }));
        let stop = false;
        const deadline = Date.now() + budgetMs;
        const worker = async () => {
            while (todo.length && !stop && Date.now() < deadline) {
                const { kind, item } = todo.shift();
                let x;
                try {
                    x = await this.lookup(kind, item);
                } catch (e) {
                    stop = true; // sin red o con el cupo agotado: lo demás, la próxima vez
                    return;
                }
                const meta = x && x.tmdbId && confidentMatch(item, x) ? { tmdb: String(x.tmdbId), poster: x.poster || '', backdrop: x.backdrop || '', plot: x.overview || '', genre: x.genres || '', rating: x.rating || 0, year: item.year || x.year || '', name: x.title || item.name } : { tmdb: '' };
                known[kind + ':' + item.id] = meta;
                Object.assign(item, meta);
            }
        };
        if (todo.length) {
            await Promise.all([worker(), worker(), worker(), worker()]);
            await db.put('meta', key, known).catch(() => {});
        }
        return data;
    }

    /**
     * Los ficheros no tienen ficha propia: la completa TMDB a partir del título. Los episodios ya
     * vienen en el catálogo; si quien pregunta no los trae (la serie como respaldo de la de otro
     * proveedor), se buscan en el catálogo guardado de esta carpeta.
     */
    async info(kind, id, base) {
        if (kind !== 'series') return Object.assign({}, base);
        let seasons = base && Array.isArray(base.seasons) && base.seasons.length ? base.seasons : null;
        if (!seasons) {
            const saved = await catalogStore.load(this.profile.id).catch(() => null);
            const own = saved && (saved.series || []).find((s) => String(s.id) === String(id));
            seasons = own ? own.seasons : [];
        }
        // Copia: quien recibe la ficha marca los episodios con su proveedor.
        return Object.assign({}, base, { seasons: seasons.map((s) => Object.assign({}, s, { episodes: s.episodes.map((e) => Object.assign({}, e)) })) });
    }

    vodUrl(id, ext, item) {
        return item && item.url ? item.url : '';
    }

    seriesUrl(id, ext, item) {
        return item && item.url ? item.url : '';
    }

    liveUrl() {
        return '';
    }

    xmltvUrl() {
        return '';
    }
}
